import React, { useState, useEffect, useMemo } from "react";
import { RootState } from "../../../utils/store";
import { ChatSelectMenu } from "./ChatSelectMenu";
import { ChatMessageForm } from "./ChatMessageForm";
import {
  useCreateChatMutation,
  useDeleteChatMutation,
} from "../../../api/chatApi";
import { ChatRequestTaskQue } from "../../../api/chatRequestTaskQueue";
import type { Chat, Message } from "./types";
import { fixLongName } from "../../../products/copy-assistant/utils";
import { ChatMessageList } from "./ChatMessageList";
import { useSelector } from "react-redux";

type ChatLayoutProps = {
  chats: string;
};

export const ChatLayout: React.FC<ChatLayoutProps> = ({ chats: chatList }) => {
  const getInitialChatId = (): number | null => {
    const parsedChats = JSON.parse(chatList || "[]");
    const urlParams = new URLSearchParams(window.location.search);
    const chatParam = parseInt(urlParams.get("id"));
    if (chatParam && parsedChats.find((c: Chat) => c.id === chatParam)) {
      return chatParam;
    }
    return parsedChats.length > 0 ? parsedChats[0].id : null;
  };

  const token = useSelector((state: RootState) => state.auth.token);
  const [createChatRequest, { isLoading }] = useCreateChatMutation();
  const [deleteChatRequest] = useDeleteChatMutation();
  const [chats, setChats] = useState<Chat[]>(JSON.parse(chatList || "[]"));
  const [activeChatId, setActiveChatId] = useState<number | null>(
    getInitialChatId()
  );

  const activeChat = chats.find((c) => c.id === activeChatId) || null;

  const chatRequestTaskQue = useMemo(() => {
    if (token) return new ChatRequestTaskQue(token);
  }, [token]);

  useEffect(() => {
    if (activeChatId) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set("id", activeChatId.toString());
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${urlParams}`
      );
    }
  }, [activeChatId]);

  const handleChatSelect = (chatId: number): void => {
    setActiveChatId(chatId);
  };

  const handleChatCreate = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    try {
      const response = await createChatRequest({}).unwrap();

      setChats([
        {
          id: response.id,
          modelName: response.model_name,
          messages: [],
        },
        ...chats,
      ]);
      setActiveChatId(response.id);
    } catch (error) {
      console.error(error);
    }
  };

  const handleChatDelete = async (chatId: number): Promise<void> => {
    if (isLoading) {
      return;
    }

    try {
      await deleteChatRequest({
        chat_id: chatId,
      });

      setChats((l) => l.filter((item) => item.id != chatId));
    } catch (error) {
      console.error(error);
    }
  };

  const handleMessageAdded = (newMessage: Message): void => {
    setChats((prevChats) =>
      prevChats.map((chat) =>
        chat.id === activeChatId
          ? { ...chat, messages: [...chat.messages, newMessage] }
          : chat
      )
    );
  };

  return (
    <div className="txu-form tw-grid tw-grid-rows-[min-content_auto] tw-grid-cols-[auto_1fr] tw-w-full tw-h-full-min-header tw-gap-6 tw-pt-8 tw-mx-auto !tw-pl-6">
      <div className="tw-row-start-2 tw-col-start-1">
        <ChatSelectMenu
          summaries={chats.map((chat) => ({
            name: chat.messages[0]
              ? fixLongName(chat.messages[0].text, 24)
              : "Unnamed chat",
            id: chat.id,
            timeSinceActive: chat.messages[chat.messages.length - 1]?.timeStamp,
          }))}
          onChatSelect={handleChatSelect}
          onChatCreate={handleChatCreate}
          onChatDelete={handleChatDelete}
          activeChatId={activeChatId || undefined}
        />
      </div>

      <header className="tw-flex-none tw-col-start-2 tw-w-full">
        <h1 className="tw-flex tw-items-center tw-m-0 tw-text-2xl">
          <i className="material-icons tw-leading-[1]">chat_bubble</i>
          <span className="tw-ml-1">GPT chat</span>
        </h1>
        <span>Create compelling product descriptions and marketing copy</span>
      </header>

      <div className="tw-col-start-2 tw-min-h-0 tw-overflow-hidden">
        <div className="tw-flex tw-flex-col tw-h-full tw-min-h-0 tw-p-8 tw-border-t tw-border-l">
          <div className="tw-flex tw-flex-col tw-w-full tw-max-w-[1000px] tw-flex-1 tw-min-h-0 tw-mx-auto">
            {activeChat ? (
              <>
                <section
                  className="tw-pr-2 tw-grow tw-flex-1 tw-w-full tw-min-h-0 tw-pb-32 tw-overflow-y-auto"
                  style={{ scrollbarGutter: "stable" }}
                >
                  <div className="tw-mx-auto tw-mb-8 tw-text-center tw-text-gray-500">
                    ID#{activeChatId}
                  </div>
                  <ChatMessageList messages={activeChat.messages} />
                </section>
                <ChatMessageForm
                  chatId={activeChatId}
                  chatRequestTaskQue={chatRequestTaskQue}
                  modelName={activeChat.modelName}
                  onMessageAdded={handleMessageAdded}
                />
              </>
            ) : (
              <p>No chat selected. Please select a chat or create a new one.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
